import React, { useState, useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useLocation,
} from 'react-router-dom';
import Dashboard from './Pages/Dashboard/Dashboard';
import Conversations from './Pages/Message/Conversations';
import Documents from './Pages/Documents/Documents';
import NotFound from './Pages/NotFound';
import Login from './Pages/Login/Login';
import ForgotPassword from './Pages/Login/ForgotPassword';
import ResetPassword from './Pages/Login/ResetPassword';
import ChangePassword from './Pages/Login/ChangePassword';
import Training from './Pages/Training/Training';
import TrainingCreate from './Pages/Training/TrainingCreate';
import TrainingEdit from './Pages/Training/TrainingEdit';
import ReleaseNotes from './Pages/Releases/ReleaseNotes';
import { Box } from '@mui/material';
import { hasPageToken } from './utils/pageTokens';
import { canAccessHub, getDefaultRoute } from './utils/personas';
import Footer from './Footer';
import HubLayout from './layouts/HubLayout';
import PortalLayout from './layouts/PortalLayout';
import LoansLayout from './layouts/LoansLayout';
import ApplicationsPortal from './Pages/Portal/ApplicationsPortal';
import StartApplicationPortal from './Pages/Portal/StartApplicationPortal';
import AccountPortal from './Pages/Portal/AccountPortal';
import LoanHome from './Pages/Portal/loans/LoanHome';
import LoanPay from './Pages/Portal/loans/LoanPay';
import LoanPayConfirm from './Pages/Portal/loans/LoanPayConfirm';
import LoanAutopay from './Pages/Portal/loans/LoanAutopay';
import LoanDocuments from './Pages/Portal/loans/LoanDocuments';
import LoanHistory from './Pages/Portal/loans/LoanHistory';
import TrainingDetail from './Pages/Training/TrainingDetail';
import Donations from './Pages/Donations/public/Donations';
import UserList from './Pages/Users/UserList';
import { applications } from './utils/variables';
import LCChapterList from './Pages/Chapters/LocalChapter/LCChapterList';
import SPDChapterList from './Pages/Chapters/SPDChapter/SPDChapterList';
import BnBList from './Pages/BedAndBreakfast/BnBList';
import BnBCreate from './Pages/BedAndBreakfast/BnBCreate';
import DonationsList from './Pages/Donations/internal/DonationsList';
import ReciprocityDetail from './Pages/Chapters/Reciprocities/ReciprocityDetail';
import ReciprocityList from './Pages/Chapters/Reciprocities/ReciprocityList';
import TLCList from './Pages/TLC/TLCList';
import TLCCreate from './Pages/TLC/TLCCreate';
import ELFLanding from './Pages/Projects/ELF';
import StarLandingPage from './Pages/Projects/Star';
import PCELandingPage from './Pages/Projects/PCE';
import PSALandingPage from './Pages/Projects/PSA';
import IPSLandingPage from './Pages/Projects/IPS';
import FoundationLandingPage from './Pages/Projects/Foundation';
import CanViewFoundationAwards from './Pages/Projects/Foundation/CanViewFoundationAwards';
import FoundationApplicantsList from './Pages/Projects/Foundation/ApplicantsList';
import FoundationApplicantCreate from './Pages/Projects/Foundation/ApplicantCreate';
import FoundationApplicantDetail from './Pages/Projects/Foundation/ApplicantDetail';
import FoundationAwardsList from './Pages/Projects/Foundation/AwardsList';
import {
  MarketplaceList,
  MarketplaceDetail,
  MarketplaceCreate,
  MarketplaceEdit,
} from './Pages/Marketplace';
import NewsfeedList from './Pages/Newsfeed/NewsfeedList';
import NewsfeedDetail from './Pages/Newsfeed/NewsfeedDetail';
import NewsfeedCreate from './Pages/Newsfeed/NewsfeedCreate';
import NewsFeedView from './Pages/Newsfeed/NewsFeedView';
import DonationDetail from './Pages/Donations/internal/DonationDetail';
import DonationAckBatchesList from './Pages/Donations/internal/DonationAckBatchesList';
import DonationAckBatchDetailPage from './Pages/Donations/internal/DonationAckBatchDetailPage';
import InternalDonationNew from './Pages/Donations/internal/InternalDonationNew';
import InternalDonationEdit from './Pages/Donations/internal/InternalDonationEdit';
import UserDetail from './Pages/Users/UserDetail';
import IntroductionList from './Pages/Users/IntroOfWoman/IntroductionList';
import IntroductionDetail from './Pages/Users/IntroOfWoman/IntroductionDetail';
import IntroductionForm from './Pages/Users/IntroOfWoman/IntroductionForm';
import SPDChapterDetail from './Pages/Chapters/SPDChapter/SPDChapterDetail';
import LCDetail from './Pages/Chapters/LocalChapter/LCDetail';
import EventDetailPage from './Pages/Chapters/LocalChapter/EventDetailPage';
import ElectionOfOfficersWizard from './Pages/Chapters/LocalChapter/ElectionOfOfficersWizard';
import LCCommitteesPage from './Pages/Chapters/LocalChapter/LCCommitteesPage';
import LCSocialPage from './Pages/Chapters/LocalChapter/LCSocialPage';
import LCEventsPage from './Pages/Chapters/LocalChapter/LCEventsPage';
import LCBylawsPage from './Pages/Chapters/LocalChapter/LCBylawsPage';
import { getMe } from './endpoints/login';
import { jwtDecode } from 'jwt-decode';
import { useApiGet } from './utils/httpClient';
import TLCDetail from './Pages/TLC/TLCDetail';
import BnBDetail from './Pages/BedAndBreakfast/BnBDetail';
import Cottey from './Pages/Projects/Cottey';
import IntlDetail from './Pages/Chapters/International/InternationalDetail';
import FeedbackCreate from './Pages/Feedback/ContactUs';
import SeekingTransferList from './Pages/Users/SeekingTransfer/SeekingTransferList';
import SeekingTransferDetail from './Pages/Users/SeekingTransfer/SeekingTransferDetail';
import SeekingTransferCreate from './Pages/Users/SeekingTransfer/SeekingTransferCreate';
import ConversationDetail from './Pages/Message/ConversationDetail';
import DocumentLibrary from './Pages/Library/DocumentLibrary';
import DocumentManager from './Pages/Library/DocumentManager';
import DocumentView from './Pages/Library/DocumentView';
import DocumentViewLatest from './Pages/Library/DocumentViewLatest';
import DocumentVersionDetail from './Pages/Library/DocumentVersionDetail';
import DocumentCreate from './Pages/Library/DocumentCreate';
import DocumentEdit from './Pages/Library/DocumentEdit';
import DocumentBundle from './Pages/Library/DocumentBundle';
import LeadershipDevelopment from './Pages/LDC/LeadershipDevelopment';
import MembershipResources from './Pages/Membership/MembershipResources';
import CanViewMembershipResources from './Pages/Membership/CanViewMembershipResources';
import Map from './Pages/Maps/Map';
import { config } from './utils/config';
import AnnualReportsDashboard from './Pages/AnnualReports/AnnualReportsDashboard';
import Prework from './Pages/AnnualReports/Prework';
import RosterDuesLanding from './Pages/AnnualReports/RosterDues/RosterDuesLanding';
import RosterDuesList from './Pages/AnnualReports/RosterDues/RosterDuesList';
import RosterDuesMemberList from './Pages/AnnualReports/RosterDues/RosterDuesMemberList';
import RosterDuesSummary from './Pages/AnnualReports/RosterDues/RosterDuesSummary';
import RosterDuesChapterList from './Pages/AnnualReports/RosterDues/RosterDuesChapterList';
import RosterDuesSPDSummary from './Pages/AnnualReports/RosterDues/RosterDuesSPDSummary';
import IRSFormsLanding from './Pages/AnnualReports/IRSForms/IRSFormsLanding';
import IRSFormsList from './Pages/AnnualReports/IRSForms/IRSFormsList';
import IRSFormsDetail from './Pages/AnnualReports/IRSForms/IRSFormsDetail';
import ARResources from './Pages/AnnualReports/Resources';
import ARTraining from './Pages/AnnualReports/Training';
import SPDPreworkForm from './Pages/AnnualReports/Prework/SPDPreworkForm';
import SPDPreworkList from './Pages/AnnualReports/Prework/SPDPreworkList';
import PEOReportsList from './Pages/PEOReports/PEOReportsList';
import PEOReportDetail from './Pages/PEOReports/PEOReportDetail';
import Permissions from './Pages/Administration/Permissions';
import ExecutiveOffice from './Pages/Administration/ExecutiveOffice';
import ScheduledTasks from './Pages/Administration/ScheduledTasks';
import EmailCampaignsList from './Pages/Administration/EmailCampaignsList';
import EmailCampaignDetail from './Pages/Administration/EmailCampaignDetail';
import NoticeManagementList from './Pages/Administration/NoticeManagementList';
import NoticeManagementCreate from './Pages/Administration/NoticeManagementCreate';
import NoticeManagementEdit from './Pages/Administration/NoticeManagementEdit';
import LoansTestList from './Pages/Administration/LoansTestList';
import LoansTestCreate from './Pages/Administration/LoansTestCreate';
import LoansTestDetail from './Pages/Administration/LoansTestDetail';
import LoansTestUserDetail from './Pages/Administration/LoansTestUserDetail';
import LockdownVaultTest from './Pages/Administration/LockdownVaultTest';
import ChapterSuppliesLanding from './Pages/ChapterSuppliesManagement/ChapterSuppliesLanding';
import ItemList from './Pages/ChapterSuppliesManagement/Items/ItemList';
import ItemDetail from './Pages/ChapterSuppliesManagement/Items/ItemDetail';
import ItemCreate from './Pages/ChapterSuppliesManagement/Items/ItemCreate';
import VendorList from './Pages/ChapterSuppliesManagement/Vendors/VendorList';
import VendorCreate from './Pages/ChapterSuppliesManagement/Vendors/VendorCreate';
import VendorDetail from './Pages/ChapterSuppliesManagement/Vendors/VendorDetail';
import OnlineItemList from './Pages/ChapterSuppliesManagement/OnlineItems/OnlineItemList';
import OnlineItemCreate from './Pages/ChapterSuppliesManagement/OnlineItems/OnlineItemCreate';
import OnlineItemDetail from './Pages/ChapterSuppliesManagement/OnlineItems/OnlineItemDetail';
import InventoryPage from './Pages/ChapterSuppliesManagement/Inventory/InventoryPage';
import PlaceholderPage from './Pages/ChapterSuppliesManagement/PlaceholderPage';
import ManagementOrdersList from './Pages/ChapterSuppliesManagement/Orders/ManagementOrdersList';
import ManagementMailInCheckOrder from './Pages/ChapterSuppliesManagement/Orders/ManagementMailInCheckOrder';
import ManagementOrderDetail from './Pages/ChapterSuppliesManagement/Orders/ManagementOrderDetail';
import VendorOrderBatchesList from './Pages/ChapterSuppliesManagement/VendorBatches/VendorOrderBatchesList';
import VendorOrderBatchDetail from './Pages/ChapterSuppliesManagement/VendorBatches/VendorOrderBatchDetail';
import FundsList from './Pages/Finance/FundsList';
import FundDetail from './Pages/Finance/FundDetail';
import FundCreate from './Pages/Finance/FundCreate';
import ElfLoansList from './Pages/Finance/ElfLoansList';
import ElfLoanDetail from './Pages/Finance/ElfLoanDetail';
import ElfPaymentsLayout from './Pages/Finance/ElfPaymentsLayout';
import ElfPaymentsPayPalPage from './Pages/Finance/ElfPaymentsPayPalPage';
import ElfPaymentsPayPalDetailPage from './Pages/Finance/ElfPaymentsPayPalDetailPage';
import ElfPaymentsPayPalActivityDetailPage from './Pages/Finance/ElfPaymentsPayPalActivityDetailPage';
import ElfPaymentsCheckBatchesPage from './Pages/Finance/ElfPaymentsCheckBatchesPage';
import ElfPaymentsCheckBatchDetailPage from './Pages/Finance/ElfPaymentsCheckBatchDetailPage';
import PaymentsLayout from './Pages/Finance/PaymentsLayout';
import PaymentsStripePage from './Pages/Finance/PaymentsStripePage';
import PaymentsPayPalPage from './Pages/Finance/PaymentsPayPalPage';
import PaymentsCheckBatchesPage from './Pages/Finance/PaymentsCheckBatchesPage';
import PaymentsCheckBatchDetailPage from './Pages/Finance/PaymentsCheckBatchDetailPage';
import PaymentsPayoutsListPage from './Pages/Finance/PaymentsPayoutsListPage';
import PaymentsPayoutDetailPage from './Pages/Finance/PaymentsPayoutDetailPage';
import OrderingUnderConstruction from './Pages/OrderingUnderConstruction';
import StorefrontList from './Pages/ChapterSuppliesStorefront/StorefrontList';
import StorefrontDetail from './Pages/ChapterSuppliesStorefront/StorefrontDetail';
import StorefrontCart from './Pages/ChapterSuppliesStorefront/StorefrontCart';
import StorefrontCheckout from './Pages/ChapterSuppliesStorefront/StorefrontCheckout';
import StorefrontConfirmation from './Pages/ChapterSuppliesStorefront/StorefrontConfirmation';
import StorefrontOrders from './Pages/ChapterSuppliesStorefront/StorefrontOrders';
import StorefrontOrderDetail from './Pages/ChapterSuppliesStorefront/StorefrontOrderDetail';

const PrivateRoutes = () => {
  const location = useLocation();
  // Check auth in render to ensure it's reactive
  const auth = localStorage.getItem('user_id');

  if (!auth) {
    // Don't capture login page as returnUrl (would cause infinite loop)
    if (location.pathname === '/login') {
      return <Navigate to="/login" replace />;
    }

    // Save the intended destination before redirecting to login
    const returnUrl = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
        replace
      />
    );
  }

  return <Outlet />;
};

const IsNewsfeedAdmin = () => {
  let apps = localStorage.getItem('apps');
  if (!apps) return false;
  let allowed = apps.includes(applications.NewsfeedAdmin);
  return allowed ? <Outlet /> : <Navigate to="/" />;
};

const IsDocumentAdmin = () => {
  let apps = localStorage.getItem('apps');
  if (!apps) return false;
  let allowed = apps.includes(applications.DocumentAdmin);
  return allowed ? <Outlet /> : <Navigate to="/" />;
};

const IsAnnualReportsUser = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'annual-reports') ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const CanViewPermissions = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'permissions') ? <Outlet /> : <Navigate to="/" />;
};

const CanViewExecutiveOffice = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'exec-office') ? <Outlet /> : <Navigate to="/" />;
};

const CanViewNoticeManagement = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'notice-management') ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const CanViewChapterSuppliesManagement = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'supplies-management') ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const CanViewLoansManage = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'loan-manage') ? <Outlet /> : <Navigate to="/" />;
};

const CanViewEmailCampaigns = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'email-campaigns') ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const CanViewFinanceDonations = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'finance-donations') ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const CanViewFinanceFunds = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'finance-funds') ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const CanViewFinanceElfLoans = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'finance-elf-loans') ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const CanViewFinanceElfPayments = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'finance-elf-payments') ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const CanViewFinancePayments = () => {
  const pages = localStorage.getItem('pages');
  return hasPageToken(pages, 'finance-payments') ||
    hasPageToken(pages, 'finance-check-batches') ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const CanViewSeekingTransfer = () => {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, '') || '/';
  const isListRoute = pathname === '/seeking-transfer';

  if (isListRoute) {
    const pages = localStorage.getItem('pages');
    if (!hasPageToken(pages, 'seeking-transfer')) {
      return <Navigate to="/" />;
    }
  }

  return <Outlet />;
};

const CanAccessHub = () => {
  const personas = localStorage.getItem('personas');
  return canAccessHub(personas) ? (
    <Outlet />
  ) : (
    <Navigate to={getDefaultRoute()} replace />
  );
};

// Root layout: token refresh + footer. Hub/portal chrome lives in nested layouts.
const RootLayout = () => {
  const location = useLocation();
  const [enableGetMe, setEnableGetMe] = useState(false);

  useEffect(() => {
    const hasUserId = !!localStorage.getItem('user_id');
    const hasRefreshToken = !!localStorage.getItem('refresh');
    if (hasUserId && hasRefreshToken) {
      const timer = setTimeout(() => setEnableGetMe(true), 100);
      return () => clearTimeout(timer);
    }
    setEnableGetMe(false);
  }, [location.pathname]);

  const { data } = useApiGet(['refresh'], getMe, {
    enabled: enableGetMe,
    retry: 0,
    refetchInterval: 60 * 60000,
  });

  const loggedIn = !!localStorage.getItem('user_id');

  useEffect(() => {
    if (data) {
      try {
        localStorage.setItem('token', data.access_token);
        const decoded = jwtDecode(data.access_token);
        if (decoded.sub) localStorage.setItem('user_id', decoded.sub);
        if (decoded.first_name)
          localStorage.setItem('first_name', decoded.first_name);
        if (decoded.last_name)
          localStorage.setItem('last_name', decoded.last_name);
        if (decoded.applications?.length)
          localStorage.setItem('apps', decoded.applications.join(','));
        if (decoded.pages?.length)
          localStorage.setItem('pages', decoded.pages.join(','));
        if (decoded.personas)
          localStorage.setItem('personas', decoded.personas.join(','));
        else localStorage.removeItem('personas');
        if (decoded.default_route)
          localStorage.setItem('default_route', decoded.default_route);
        else localStorage.removeItem('default_route');
      } catch (error) {
        console.error(
          'Error decoding token or writing to localStorage:',
          error
        );
        localStorage.clear();
        window.location.href = '/login';
      }
    }
  }, [data]);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: '1 0 auto', width: '100%' }}>
        <Outlet />
      </Box>
      {loggedIn && <Footer />}
    </Box>
  );
};

// Route config for data router. Order matters for matching.
function buildRoutesConfig() {
  const privateRoutesChildren = [
    { index: true, element: <Dashboard /> },
    {
      path: 'roster',
      element: <Navigate to="/annual-reports/roster-dues" replace />,
    },
    {
      path: 'roster/:id',
      element: <Navigate to="/annual-reports/roster-dues" replace />,
    },
    {
      path: 'irs',
      element: <Navigate to="/annual-reports/irs-forms" replace />,
    },
    { path: 'reports', element: <Navigate to="/peo-reports" replace /> },
    ...(config.NEW_REPORTS_ENABLED
      ? [
          { path: 'peo-reports', element: <PEOReportsList /> },
          { path: 'peo-reports/:id', element: <PEOReportDetail /> },
        ]
      : []),
    {
      path: 'irs/:id',
      element: <Navigate to="/annual-reports/irs-forms" replace />,
    },
    { path: 'training', element: <Training /> },
    { path: 'training/create', element: <TrainingCreate /> },
    { path: 'training/:id/edit', element: <TrainingEdit /> },
    { path: 'training/:id', element: <TrainingDetail /> },
    { path: 'documents', element: <Documents /> },
    {
      element: <IsDocumentAdmin />,
      children: [
        { path: 'library/create', element: <DocumentCreate /> },
        { path: 'library/:id/versions', element: <DocumentVersionDetail /> },
        {
          path: 'library/:id/versions/:versionId',
          element: <DocumentVersionDetail />,
        },
        { path: 'library/:id/edit', element: <DocumentEdit /> },
        { path: 'library/manager', element: <DocumentManager /> },
      ],
    },
    { path: 'library/document/:id/latest', element: <DocumentViewLatest /> },
    { path: 'library/:id/view/:versionId', element: <DocumentView /> },
    { path: 'library/bundles/:bundleId', element: <DocumentBundle /> },
    { path: 'library', element: <DocumentLibrary /> },
    { path: 'release-notes', element: <ReleaseNotes /> },
    { path: 'local-chapters', element: <LCChapterList /> },
    {
      path: 'local-chapters/:id/election-of-officers',
      element: <ElectionOfOfficersWizard />,
    },
    {
      path: 'local-chapters/:id/events/:eventId',
      element: <EventDetailPage />,
    },
    {
      path: 'local-chapters/:id/committees',
      element: <LCCommitteesPage />,
    },
    {
      path: 'local-chapters/:id/social',
      element: <LCSocialPage />,
    },
    {
      path: 'local-chapters/:id/events',
      element: <LCEventsPage />,
    },
    {
      path: 'local-chapters/:id/bylaws',
      element: <LCBylawsPage documentType="by_laws" />,
    },
    {
      path: 'local-chapters/:id/standing-rules',
      element: <LCBylawsPage documentType="standing_rules" />,
    },
    { path: 'local-chapters/:id', element: <LCDetail /> },
    { path: 'spd-chapters', element: <SPDChapterList /> },
    { path: 'spd-chapters/:id', element: <SPDChapterDetail /> },
    { path: 'international', element: <IntlDetail /> },
    { path: 'bnb', element: <BnBList /> },
    { path: 'bnb/create', element: <BnBCreate /> },
    { path: 'bnb/:id', element: <BnBDetail /> },
    {
      element: <CanViewFinanceDonations />,
      children: [
        { path: 'internal/donations', element: <DonationsList /> },
        { path: 'internal/donations/new', element: <InternalDonationNew /> },
        {
          path: 'internal/donations/ack-batches',
          element: <DonationAckBatchesList />,
        },
        {
          path: 'internal/donations/ack-batches/:batchId',
          element: <DonationAckBatchDetailPage />,
        },
        {
          path: 'internal/donations/:id/edit',
          element: <InternalDonationEdit />,
        },
        { path: 'internal/donations/:id', element: <DonationDetail /> },
      ],
    },
    {
      element: <CanViewFinanceFunds />,
      children: [
        {
          path: 'finance/funds',
          element: <FundsList />,
        },
        {
          path: 'finance/funds/new',
          element: <FundCreate />,
        },
        {
          path: 'finance/funds/:id',
          element: <FundDetail />,
        },
      ],
    },
    {
      element: <CanViewFinanceElfLoans />,
      children: [
        {
          path: 'finance/elf-loans',
          element: <ElfLoansList />,
        },
        {
          path: 'finance/elf-loans/:id',
          element: <ElfLoanDetail />,
        },
      ],
    },
    {
      element: <CanViewFinanceElfPayments />,
      children: [
        {
          path: 'finance/elf-payments',
          element: <ElfPaymentsLayout />,
          children: [
            { index: true, element: <Navigate to="paypal" replace /> },
            { path: 'paypal', element: <ElfPaymentsPayPalPage /> },
            {
              path: 'paypal/activity/:id',
              element: <ElfPaymentsPayPalActivityDetailPage />,
            },
            {
              path: 'paypal/:id',
              element: <ElfPaymentsPayPalDetailPage />,
            },
            {
              path: 'check-batches',
              element: <ElfPaymentsCheckBatchesPage />,
            },
            {
              path: 'check-batches/:batchId',
              element: <ElfPaymentsCheckBatchDetailPage />,
            },
          ],
        },
      ],
    },
    {
      path: 'finance/check-batches',
      element: <Navigate to="/finance/payments/check-batches" replace />,
    },
    {
      element: <CanViewFinancePayments />,
      children: [
        {
          path: 'finance/payments',
          element: <PaymentsLayout />,
          children: [
            { index: true, element: <Navigate to="stripe" replace /> },
            { path: 'stripe', element: <PaymentsStripePage /> },
            { path: 'paypal', element: <PaymentsPayPalPage /> },
            { path: 'check-batches', element: <PaymentsCheckBatchesPage /> },
            { path: 'payouts', element: <PaymentsPayoutsListPage /> },
            {
              path: 'payouts/:payoutId',
              element: <PaymentsPayoutDetailPage />,
            },
            {
              path: 'check-batches/:batchId',
              element: <PaymentsCheckBatchDetailPage />,
            },
          ],
        },
      ],
    },
    { path: 'leadership-development', element: <LeadershipDevelopment /> },
    {
      element: <CanViewMembershipResources />,
      children: [
        {
          path: 'membership-resources',
          element: <MembershipResources />,
        },
      ],
    },
    { path: 'tlc', element: <TLCList /> },
    { path: 'tlc/new', element: <TLCCreate /> },
    { path: 'tlc/:id', element: <TLCDetail /> },
    { path: 'user', element: <UserList /> },
    { path: 'user/:id', element: <UserDetail /> },
    { path: 'introductions', element: <IntroductionList /> },
    { path: 'introductions/:id', element: <IntroductionDetail /> },
    { path: 'introductions/create', element: <IntroductionForm /> },
    { path: 'reciprocity', element: <ReciprocityList /> },
    { path: 'reciprocity/:id', element: <ReciprocityDetail /> },
    { path: 'projects/elf', element: <ELFLanding /> },
    { path: 'projects/star', element: <StarLandingPage /> },
    { path: 'projects/pce', element: <PCELandingPage /> },
    { path: 'projects/psa', element: <PSALandingPage /> },
    { path: 'projects/ips', element: <IPSLandingPage /> },
    {
      element: <IsNewsfeedAdmin />,
      children: [
        { path: 'newsfeed', element: <NewsfeedList /> },
        { path: 'newsfeed/:id', element: <NewsfeedDetail /> },
        { path: 'newsfeed/create', element: <NewsfeedCreate /> },
      ],
    },
    { path: 'newsfeed/view/:id', element: <NewsFeedView /> },
    {
      element: <CanViewSeekingTransfer />,
      children: [
        { path: 'seeking-transfer', element: <SeekingTransferList /> },
        { path: 'seeking-transfer/:id', element: <SeekingTransferDetail /> },
        {
          path: 'seeking-transfer/create/:userId',
          element: <SeekingTransferCreate />,
        },
      ],
    },
    { path: 'foundation', element: <FoundationLandingPage /> },
    {
      element: <CanViewFoundationAwards />,
      children: [
        {
          path: 'foundation/applicants',
          element: <FoundationApplicantsList />,
        },
        {
          path: 'foundation/applicants/new',
          element: <FoundationApplicantCreate />,
        },
        {
          path: 'foundation/applicants/:id',
          element: <FoundationApplicantDetail />,
        },
        { path: 'foundation/awards', element: <FoundationAwardsList /> },
      ],
    },
    { path: 'cottey', element: <Cottey /> },
    { path: 'marketplace', element: <MarketplaceList /> },
    { path: 'marketplace/:id', element: <MarketplaceDetail /> },
    { path: 'marketplace/create', element: <MarketplaceCreate /> },
    { path: 'marketplace/:id/edit', element: <MarketplaceEdit /> },
    {
      path: 'ordering_under_construction',
      element: <OrderingUnderConstruction />,
    },
    {
      path: 'chapter-supplies/storefront',
      element: <StorefrontList />,
    },
    {
      path: 'chapter-supplies/storefront/items/:id',
      element: <StorefrontDetail />,
    },
    {
      path: 'chapter-supplies/storefront/cart',
      element: <StorefrontCart />,
    },
    {
      path: 'chapter-supplies/storefront/checkout',
      element: <StorefrontCheckout />,
    },
    {
      path: 'chapter-supplies/storefront/confirmation',
      element: <StorefrontConfirmation />,
    },
    {
      path: 'chapter-supplies/storefront/orders',
      element: <StorefrontOrders />,
    },
    {
      path: 'chapter-supplies/storefront/orders/:id',
      element: <StorefrontOrderDetail />,
    },
    {
      element: <CanViewChapterSuppliesManagement />,
      children: [
        {
          path: 'chapter-supplies-management',
          element: <ChapterSuppliesLanding />,
        },
        { path: 'chapter-supplies-management/items', element: <ItemList /> },
        {
          path: 'chapter-supplies-management/items/create',
          element: <ItemCreate />,
        },
        {
          path: 'chapter-supplies-management/items/:id',
          element: <ItemDetail />,
        },
        {
          path: 'chapter-supplies-management/vendors',
          element: <VendorList />,
        },
        {
          path: 'chapter-supplies-management/vendors/create',
          element: <VendorCreate />,
        },
        {
          path: 'chapter-supplies-management/vendors/:id',
          element: <VendorDetail />,
        },
        {
          path: 'chapter-supplies-management/purchase-orders',
          element: <PlaceholderPage title="Purchase Orders" />,
        },
        {
          path: 'chapter-supplies-management/inventory',
          element: <InventoryPage />,
        },
        {
          path: 'chapter-supplies-management/online-items',
          element: <OnlineItemList />,
        },
        {
          path: 'chapter-supplies-management/online-items/create',
          element: <OnlineItemCreate />,
        },
        {
          path: 'chapter-supplies-management/online-items/:id',
          element: <OnlineItemDetail />,
        },
        {
          path: 'chapter-supplies-management/orders',
          element: <ManagementOrdersList />,
        },
        {
          path: 'chapter-supplies-management/orders/mail-in',
          element: <ManagementMailInCheckOrder />,
        },
        {
          path: 'chapter-supplies-management/orders/mail-in-check',
          element: (
            <Navigate
              to="/chapter-supplies-management/orders/mail-in"
              replace
            />
          ),
        },
        {
          path: 'chapter-supplies-management/orders/:id',
          element: <ManagementOrderDetail />,
        },
        {
          path: 'chapter-supplies-management/vendor-batches',
          element: <VendorOrderBatchesList />,
        },
        {
          path: 'chapter-supplies-management/vendor-batches/:id',
          element: <VendorOrderBatchDetail />,
        },
      ],
    },
    { path: 'conversations', element: <Conversations /> },
    { path: 'conversations/:id', element: <ConversationDetail /> },
    { path: 'feedback/create', element: <FeedbackCreate /> },
    { path: 'map', element: <Map /> },
    {
      element: <IsAnnualReportsUser />,
      children: [
        { path: 'annual-reports', element: <AnnualReportsDashboard /> },
        { path: 'annual-reports/prework', element: <Prework /> },
        { path: 'annual-reports/prework/spd', element: <SPDPreworkList /> },
        { path: 'prework/spd/:id', element: <SPDPreworkForm /> },
        { path: 'annual-reports/prework/spd/:id', element: <SPDPreworkForm /> },
        {
          path: 'annual-reports/prework/spd-dues/:id',
          element: <SPDPreworkForm />,
        },
        { path: 'annual-reports/roster-dues', element: <RosterDuesLanding /> },
        {
          path: 'annual-reports/roster-dues/local-chapters',
          element: <RosterDuesList formType="local_chapter" />,
        },
        {
          path: 'annual-reports/roster-dues/spd',
          element: <RosterDuesList formType="spd" />,
        },
        {
          path: 'annual-reports/roster-dues/:id/members',
          element: <RosterDuesMemberList />,
        },
        {
          path: 'annual-reports/roster-dues/:id/chapters',
          element: <RosterDuesChapterList />,
        },
        {
          path: 'annual-reports/roster-dues/:id/summary',
          element: <RosterDuesSummary />,
        },
        {
          path: 'annual-reports/roster-dues/:id/spd-summary',
          element: <RosterDuesSPDSummary />,
        },
        { path: 'annual-reports/irs-forms', element: <IRSFormsLanding /> },
        {
          path: 'annual-reports/irs-forms/:year/local-chapters',
          element: <IRSFormsList formType="lc" />,
        },
        {
          path: 'annual-reports/irs-forms/:year/spd',
          element: <IRSFormsList formType="sc" />,
        },
        {
          path: 'annual-reports/irs-forms/:year/reciprocity',
          element: <IRSFormsList formType="rg" />,
        },
        {
          path: 'annual-reports/irs-forms/:year/local-chapters/:id',
          element: <IRSFormsDetail />,
        },
        {
          path: 'annual-reports/irs-forms/:year/spd/:id',
          element: <IRSFormsDetail />,
        },
        {
          path: 'annual-reports/irs-forms/:year/reciprocity/:id',
          element: <IRSFormsDetail />,
        },
        { path: 'annual-reports/resources', element: <ARResources /> },
        { path: 'annual-reports/training', element: <ARTraining /> },
      ],
    },
    {
      element: <CanViewPermissions />,
      children: [
        { path: 'administration/permissions', element: <Permissions /> },
        { path: 'administration/scheduled-tasks', element: <ScheduledTasks /> },
      ],
    },
    {
      element: <CanViewExecutiveOffice />,
      children: [
        {
          path: 'administration/executive-office',
          element: <ExecutiveOffice />,
        },
      ],
    },
    {
      element: <CanViewNoticeManagement />,
      children: [
        {
          path: 'administration/notice-management',
          element: <NoticeManagementList />,
        },
        {
          path: 'administration/notice-management/new',
          element: <NoticeManagementCreate />,
        },
        {
          path: 'administration/notice-management/:id',
          element: <NoticeManagementEdit />,
        },
      ],
    },
    ...(config.ENABLE_LOCKDOWN_TEST
      ? [
          {
            path: 'administration/lockdown-vault-test',
            element: <LockdownVaultTest />,
          },
        ]
      : []),
    {
      element: <CanViewLoansManage />,
      children: [
        { path: 'administration/loans-test', element: <LoansTestList /> },
        {
          path: 'administration/loans-test/user/:userId',
          element: <LoansTestUserDetail />,
        },
        {
          path: 'administration/loans-test/create',
          element: <LoansTestCreate />,
        },
        { path: 'administration/loans-test/:id', element: <LoansTestDetail /> },
      ],
    },
    {
      element: <CanViewEmailCampaigns />,
      children: [
        {
          path: 'administration/email-campaigns',
          element: <EmailCampaignsList />,
        },
        {
          path: 'administration/email-campaigns/new',
          element: <EmailCampaignsList />,
        },
        {
          path: 'administration/email-campaigns/:id',
          element: <EmailCampaignDetail />,
        },
      ],
    },
  ];

  return [
    { path: 'login', element: <Login /> },
    { path: 'forgot-password', element: <ForgotPassword /> },
    { path: 'reset-password/:token', element: <ResetPassword /> },
    { path: 'change-password', element: <ChangePassword /> },
    { path: 'donations', element: <Donations /> },
    {
      element: <PrivateRoutes />,
      children: [
        {
          element: <CanAccessHub />,
          children: [
            {
              element: <HubLayout />,
              children: privateRoutesChildren,
            },
          ],
        },
        {
          path: 'portal',
          element: <PortalLayout />,
          children: [
            { path: 'applications', element: <ApplicationsPortal /> },
            {
              path: 'applications/start',
              element: <StartApplicationPortal />,
            },
            {
              path: 'loans',
              element: <LoansLayout />,
              children: [
                { index: true, element: <LoanHome /> },
                { path: 'pay', element: <LoanPay /> },
                { path: 'pay/confirm', element: <LoanPayConfirm /> },
                { path: 'autopay', element: <LoanAutopay /> },
                { path: 'documents', element: <LoanDocuments /> },
                { path: 'history', element: <LoanHistory /> },
              ],
            },
            { path: 'account', element: <AccountPortal /> },
          ],
        },
      ],
    },
    { path: '*', element: <NotFound /> },
  ];
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: buildRoutesConfig(),
  },
]);

const Router = () => {
  return <RouterProvider router={router} />;
};

export default Router;
